import React, { useState, useEffect } from 'react';
import clsx from 'clsx';
import { Form, Field } from 'react-final-form';
import moment from 'moment';
import { useRouteMatch } from 'react-router-dom'

import { Validators } from 'utils';
import DatePickerUI from 'components/ui/DatePicker/DatePicker';

import { Dependent } from 'types';
import { InputLabel, Select } from 'components/ui';
import DeleteModal from 'components/modals/DeleteModal/DeleteModal';
import { Input } from '../Input/Input';
import { InputError } from '../InputError/InputError';
import { Button } from '../Button/Button';
import { relations } from './RelationsData'
import styles from './DependentForm.module.scss';

export type DependentFormValues = {
  firstName: string;
  middleName: string;
  lastName: string;
  birthday: string;
  sex: string;
  age: string;
  grade: string;
  school: string;
  sport: string;
  relation: string;
};

export type DependentFormProps = {
  className?: string;
  isEditable?: boolean;
  data?: Dependent;
  onSubmit?: (value: DependentFormValues) => void;
  onCancel?: () => void;
  onDelete?: () => void;
  withLabels?: boolean;
  withDelete?: boolean;
};

export const DependentForm: React.FC<DependentFormProps> = ({
  className,
  isEditable = true,
  data,
  onSubmit,
  onCancel,
  onDelete,
  withLabels,
  withDelete,
}) => {
  const match = useRouteMatch()
  const [birthDay, setBirthDay] = useState<Date | undefined>();
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const [initialValues, setInitialValues] = useState({});

  useEffect(() => {
    if (data?.birthday) {
      setBirthDay(new Date(data.birthday));
    }
    setInitialValues({
      firstName: data?.firstName,
      middleName: data?.middleName,
      lastName: data?.lastName,
      birthday: data?.birthday,
      age: getAge(),
      grade: data?.grade,
      school: data?.school,
      sport: data?.sport,
      relation: data?.relation || ''
    });
  }, [data]);

  useEffect(() => {
    data && setBirthDay(new Date(data.birthday));
  }, [data]);

  function handleChangeBirthDay(value: Date) {
    setBirthDay(value);
  }

  function label(name: string) {
    return withLabels ? name : undefined;
  }

  function handleCloseModal() {
    setIsOpen(false);
  }

  function handleOpenModal() {
    setIsOpen(true);
  }

  function handleDelete() {
    // TODO delete dependent
    setIsOpen(false);
    onDelete && onDelete();
  }

  const getAge = () => {
    let date = birthDay || data?.birthday;
    var years = moment().diff(moment(date, process.env.REACT_APP_DATE_FORMAT), 'years');
    return years > 0 ? years : '';
  };

  return (
    <div className="row">
      <div className={clsx(styles.root, className, 'col-9 dependent_form_wrapper')}>
        <Form
          onSubmit={values => {
            const formValues = {
              ...values,
              birthday: birthDay ? moment(birthDay).format(process.env.REACT_APP_DATE_FORMAT) : '',
            };
            onSubmit && onSubmit(formValues as DependentFormValues);
          }}
          initialValues={initialValues}
          render={({ handleSubmit, hasValidationErrors, form }) => (
            <>
              <form onSubmit={handleSubmit} className="row">
                <Field name="firstName" validate={Validators.character}>
                  {({ input, meta }) => (
                    <div className="col-12 col-md-4 mb-4">
                      <InputLabel text='First Name' />
                      <Input
                        {...input}
                        type="text"
                        hasError={meta.error && meta.touched}
                        isDisabled={!isEditable}
                        className=""
                      />
                      {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                    </div>
                  )}
                </Field>
                <Field name="middleName">
                  {({ input }) => (
                    <div className="col-12 col-md-4 mb-4">
                      <InputLabel text='Middle Name' />
                      <Input {...input} type="text" isDisabled={!isEditable} placeholder='Optional' />
                    </div>
                  )}
                </Field>
                <Field name="lastName" validate={Validators.character}>
                  {({ input, meta }) => (
                    <div className="col-12 col-md-4 mb-4">
                      <InputLabel text='Last Name' />
                      <Input
                        {...input}
                        type="text"
                        hasError={meta.error && meta.touched}
                        isDisabled={!isEditable}
                      />
                      {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                    </div>
                  )}
                </Field>
                <Field
                  name="birthDay"
                  validate={Validators.compose(Validators.required, Validators.pastDate)}
                  render={({ input, meta }) => {
                    input.onChange(birthDay);
                    return (<div className='col-12 col-md-4'>
                      <InputLabel text='Date of Birth' />
                      <DatePickerUI
                        name='birthDay'
                        onChange={handleChangeBirthDay}
                        value={birthDay}
                        isDisabled={!isEditable}
                        futureDateNotAllowed={true}
                        withLabel={true}
                      >
                        {meta.error && birthDay && <InputError>{meta.error}</InputError>}
                      </DatePickerUI>
                    </div>);
                  }}
                />
                <Field name="age">
                  {({ input, meta }) => (
                    <div className={clsx('col-12 col-md-4 mb-4 hidden', styles.small)}>
                      <InputLabel text='Age' />
                      <Input
                        {...input}
                        type="text"
                        hasError={meta.error && meta.touched}
                        isDisabled={true}
                        value={getAge()}
                      />
                      {meta.error && meta.touched && <InputError>{meta.error}</InputError>}
                    </div>
                  )}
                </Field>
                <Field name="grade">
                  {({ input }) => (
                    <div className={clsx('col-12 col-md-4 mb-4', styles.small)}>
                      <InputLabel text='Grade' />
                      <Input {...input} type="text" isDisabled={!isEditable} placeholder="Optional"/>
                    </div>
                  )}
                </Field>
                <Field name="school">
                  {({ input }) => (
                    <div className={'col-12 col-md-4 mb-4'}>
                      <InputLabel text='School' />
                      <Input {...input} type="text" isDisabled={!isEditable} placeholder="Optional" />
                    </div>
                  )}
                </Field>
                <Field name="sport">
                  {({ input }) => (
                    <div className={'col-12 col-md-4 mb-4'}>
                      <InputLabel text='Sport(s)' />
                      <Input {...input} type="text" isDisabled={!isEditable} placeholder="Optional" />
                    </div>
                  )}
                </Field>
                <Field name="relation">
                  {({ input }) => (
                    <div className={'col-12 col-md-4 mb-4'}>
                      <InputLabel text='Relation' />
                      <Select className="rel_drop_down" options={relations} value={relations.find((item: any) => item.value == input.value)} onChange={(e: any) => input.onChange(e.value)} isDisabled={!isEditable} placeholder="Optional" />
                    </div>
                  )}
                </Field>
                {isEditable && (
                  <div className={clsx(styles.buttons, 'mb-5')}>
                    <Button
                      className={styles.submit}
                      type="bordered"
                      onClick={() => {
                        form.reset();
                        onCancel && onCancel();
                      }}
                    >
                      Cancel
                    </Button>
                    <Button className={styles.submit} htmlType="submit" disabled={hasValidationErrors}>
                      {match.url === '/dependents/add' ? 'Add dependent' : 'Save'}
                    </Button>
                    {withDelete && (
                      <Button id="delete" className={styles.submit} onClick={handleOpenModal}>
                        Remove Dependent
                      </Button>
                    )}
                  </div>
                )}
              </form>
            </>
          )}
        />
        <DeleteModal handleClose={handleCloseModal} isOpen={isOpen} handleDelete={handleDelete} name="dependent" />
      </div>
    </div>
  );
};
